import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Stack, TextField, Typography,
} from '@mui/material';
import ArrowLeftOutlinedIcon from '@mui/icons-material/ArrowLeftOutlined';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useGetData } from '../../../services/hooks/useGetData';
import { currencyFormatter } from '../../Helpers/CurrencyFormat';
import PageHeader from '../../layout/PageHeader';
import ProductRecipeEditor from './ProductRecipeEditor';
import { tokens } from '../../../theme';

const baseUrl = process.env.REACT_APP_NETLIFY_API_URL || process.env.REACT_APP_API_URL;

function Stat({ label, value, emphasize = false }) {
    return (
        <Box sx={{ minWidth: 120 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.3 }}>
                {label}
            </Typography>
            <Typography
                variant={emphasize ? 'h5' : 'body1'}
                sx={{ fontWeight: emphasize ? 700 : 600, color: 'text.primary', mt: 0.25 }}
            >
                {value}
            </Typography>
        </Box>
    );
}

export default function OpenProduct() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { id } = useParams();

    const { data: envelope, isLoading, isError, error, refetch } = useGetData(
        `products/${id}/detail`,
        { prodId: id },
        null,
        null,
        null,
        { enabled: !!id }
    );

    const product = envelope?.data;

    const [shopifyProductId, setShopifyProductId] = useState('');
    const [shopifyVariantId, setShopifyVariantId] = useState('');
    const [savingShopify, setSavingShopify] = useState(false);
    const [shopifyMsg, setShopifyMsg] = useState({ type: '', text: '' });
    const [savingRecipe, setSavingRecipe] = useState(false);
    const [recipeMsg, setRecipeMsg] = useState({ type: '', text: '' });

    useEffect(() => {
        if (!product) return;
        setShopifyProductId(product.shopifyProductId != null ? String(product.shopifyProductId) : '');
        setShopifyVariantId(product.shopifyVariantId != null ? String(product.shopifyVariantId) : '');
        setShopifyMsg({ type: '', text: '' });
        setRecipeMsg({ type: '', text: '' });
    }, [product?.itemId, product?.shopifyProductId, product?.shopifyVariantId]);

    const shopifyDirty =
        String(shopifyProductId || '') !== String(product?.shopifyProductId ?? '')
        || String(shopifyVariantId || '') !== String(product?.shopifyVariantId ?? '');

    const saveShopifyIds = async () => {
        if (!shopifyVariantId.trim()) {
            setShopifyMsg({ type: 'error', text: 'Shopify variant ID is required for inventory matching.' });
            return;
        }
        setSavingShopify(true);
        setShopifyMsg({ type: '', text: '' });
        try {
            const tkn = sessionStorage.getItem('urtkn');
            const location = localStorage.getItem('usrlctn');
            const response = await axios.put(
                `${baseUrl}/api/products/${encodeURIComponent(id)}/shopify-ids`,
                {
                    location,
                    data: {
                        shopifyProductId: shopifyProductId.trim() || null,
                        shopifyVariantId: shopifyVariantId.trim(),
                    },
                },
                { headers: { Authorization: `Bearer ${tkn}` } },
            );
            const updated = response.data?.data;
            setShopifyMsg({ type: 'success', text: 'Shopify IDs saved. Orders can match this product by variant ID.' });
            if (updated?.posItemId != null && String(updated.posItemId) !== String(id)) {
                queryClient.invalidateQueries({ queryKey: [`products/${id}/detail`] });
                navigate(`/inventory/${updated.posItemId}`, { replace: true });
            } else {
                await refetch();
            }
        } catch (err) {
            const msg =
                err?.response?.data?.error?.message
                || err?.message
                || 'Could not save Shopify IDs';
            setShopifyMsg({ type: 'error', text: msg });
        } finally {
            setSavingShopify(false);
        }
    };

    const saveRecipe = async ({ requiresSupplies, ingredients }) => {
        setSavingRecipe(true);
        setRecipeMsg({ type: '', text: '' });
        try {
            const tkn = sessionStorage.getItem('urtkn');
            const location = localStorage.getItem('usrlctn');
            await axios.put(
                `${baseUrl}/api/products/${encodeURIComponent(id)}/recipe`,
                {
                    location,
                    data: { requiresSupplies, ingredients },
                },
                { headers: { Authorization: `Bearer ${tkn}` } },
            );
            setRecipeMsg({
                type: 'success',
                text: requiresSupplies
                    ? 'Saved as Made product with recipe.'
                    : 'Saved as Whole product.',
            });
            await refetch();
            queryClient.invalidateQueries({ queryKey: [`products/${id}/detail`] });
        } catch (err) {
            const msg =
                err?.response?.data?.error?.message
                || err?.message
                || 'Could not save recipe';
            setRecipeMsg({ type: 'error', text: msg });
        } finally {
            setSavingRecipe(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
            <PageHeader
                title="Product details"
                subtitle="Live cost of goods, recipe, and channel IDs"
                actions={
                    <Button
                        size="small"
                        startIcon={<ArrowLeftOutlinedIcon fontSize="small" />}
                        onClick={() => navigate(-1)}
                        sx={{ color: 'text.secondary' }}
                    >
                        Back
                    </Button>
                }
            />

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 6, maxWidth: 880, mx: 'auto', width: '100%' }}>
                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                )}

                {isError && (
                    <Typography color="error" sx={{ mt: 3 }}>
                        {error?.message || 'Failed to load product'}
                    </Typography>
                )}

                {!isLoading && envelope?.error && (
                    <Typography color="error" sx={{ mt: 3 }}>
                        {envelope.error.message}
                    </Typography>
                )}

                {product && (
                    <Stack spacing={3} sx={{ mt: 2.5 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                            <Typography
                                sx={{
                                    fontFamily: "'Fraunces', 'Figtree', serif",
                                    fontWeight: 650,
                                    fontSize: 28,
                                    letterSpacing: '-0.02em',
                                    color: 'text.primary',
                                    mr: 1,
                                }}
                            >
                                {product.itemName}
                            </Typography>
                            <Chip
                                size="small"
                                label={product.requiresSupplies ? 'Made' : 'Whole'}
                                sx={{
                                    bgcolor: product.requiresSupplies ? tokens.okBg : '#F1EBE3',
                                    color: product.requiresSupplies ? tokens.accent : tokens.brown,
                                }}
                            />
                            {product.sellStatus && (
                                <Chip size="small" label={product.sellStatus} variant="outlined" />
                            )}
                        </Stack>

                        {product.variant && product.variant !== 'None' && (
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: -2 }}>
                                {product.variant}
                            </Typography>
                        )}

                        {product.description && (
                            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640 }}>
                                {product.description}
                            </Typography>
                        )}

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' },
                                gap: 3,
                                py: 2.5,
                                borderTop: '1px solid',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Stat label="Sale price" value={currencyFormatter(product.salePrice || 0, 2)} emphasize />
                            <Stat label="Live COGs" value={currencyFormatter(product.cogs || 0, 2)} emphasize />
                            <Stat
                                label="Unit margin"
                                value={
                                    product.marginPercent != null
                                        ? `${currencyFormatter(product.unitMargin || 0, 2)} (${product.marginPercent}%)`
                                        : currencyFormatter(product.unitMargin || 0, 2)
                                }
                            />
                            <Stat label="On hand" value={product.availForSale ?? 0} />
                            <Stat label="On shelves" value={product.totalOnShelves ?? 0} />
                        </Box>

                        <Box sx={{ py: 1 }}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="baseline"
                                gap={2}
                                sx={{ mb: 1.5 }}
                            >
                                <Box>
                                    <Typography sx={{ fontWeight: 650, fontSize: 18 }}>
                                        Shelf inventory
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: tokens.inkMuted, mt: 0.25 }}>
                                        How much of this product sits on each physical shelf
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    onClick={() => navigate('/shelving')}
                                    sx={{ color: tokens.accent, fontWeight: 650, flexShrink: 0 }}
                                >
                                    Manage shelves
                                </Button>
                            </Stack>

                            {(product.shelfLocations || []).length === 0 ? (
                                <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
                                    Not assigned to any shelf yet.
                                </Typography>
                            ) : (
                                <Box>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 88px',
                                            gap: 1,
                                            px: 0.5,
                                            py: 1,
                                            borderBottom: `1px solid ${tokens.border}`,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                fontWeight: 700,
                                                letterSpacing: '0.06em',
                                                textTransform: 'uppercase',
                                                color: tokens.inkMuted,
                                            }}
                                        >
                                            Shelf
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                fontWeight: 700,
                                                letterSpacing: '0.06em',
                                                textTransform: 'uppercase',
                                                color: tokens.inkMuted,
                                                textAlign: 'right',
                                            }}
                                        >
                                            Qty
                                        </Typography>
                                    </Box>
                                    {(product.shelfLocations || []).map((row) => (
                                        <Box
                                            key={String(row.assignmentId || row.shelfId)}
                                            onClick={() => navigate('/shelving')}
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 88px',
                                                gap: 1,
                                                alignItems: 'center',
                                                px: 0.5,
                                                py: 1.25,
                                                borderBottom: `1px solid ${tokens.border}`,
                                                cursor: 'pointer',
                                                '&:hover': { backgroundColor: 'rgba(243, 246, 241, 0.65)' },
                                            }}
                                        >
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 650, fontSize: 14, color: tokens.ink }}>
                                                    {row.shelfName}
                                                </Typography>
                                                {row.shelfCode && (
                                                    <Typography sx={{ color: tokens.inkMuted, fontSize: 12 }}>
                                                        {row.shelfCode}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontWeight: 650,
                                                    fontSize: 14,
                                                    textAlign: 'right',
                                                    color: tokens.ink,
                                                }}
                                            >
                                                {row.quantity ?? 0}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>

                        <ProductRecipeEditor
                            product={product}
                            saving={savingRecipe}
                            message={recipeMsg}
                            onSave={saveRecipe}
                        />

                        <Box
                            sx={{
                                py: 2.5,
                                px: 2.5,
                                border: `1px solid ${tokens.border}`,
                                borderRadius: 2,
                                backgroundColor: tokens.surface,
                            }}
                        >
                            <Typography sx={{ fontWeight: 650, fontSize: 18, mb: 0.5 }}>
                                Shopify mapping
                            </Typography>
                            <Typography variant="body2" sx={{ color: tokens.inkMuted, mb: 2 }}>
                                Paste the Shopify product and variant IDs so order webhooks can reduce inventory on this item.
                                Variant ID is the match key.
                            </Typography>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1.5}
                                alignItems={{ sm: 'flex-start' }}
                            >
                                <TextField
                                    label="Shopify product ID"
                                    value={shopifyProductId}
                                    onChange={(e) => setShopifyProductId(e.target.value)}
                                    size="small"
                                    fullWidth
                                    placeholder="e.g. 8123456789012"
                                    helperText="Optional — product GID or numeric ID"
                                />
                                <TextField
                                    label="Shopify variant ID"
                                    value={shopifyVariantId}
                                    onChange={(e) => setShopifyVariantId(e.target.value)}
                                    size="small"
                                    fullWidth
                                    required
                                    placeholder="e.g. 44556677889900"
                                    helperText="Required — used to match line items"
                                />
                            </Stack>
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    onClick={saveShopifyIds}
                                    disabled={savingShopify || !shopifyDirty || !shopifyVariantId.trim()}
                                >
                                    {savingShopify ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save Shopify IDs'}
                                </Button>
                                {shopifyMsg.text && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: shopifyMsg.type === 'error' ? tokens.danger : tokens.accent,
                                            fontWeight: 500,
                                        }}
                                    >
                                        {shopifyMsg.text}
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    </Stack>
                )}
            </Box>
        </Box>
    );
}
